'use client';

import clsx from 'clsx';
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { debounce } from 'lodash-es';
import { ControlButton } from '@/components/ControlButton/ControlButton';
import { ModalClose, ModalContent, ModalDescription, ModalRoot, ModalTitle, ModalTrigger } from '@/components/Modal/BaseModal';
import { type DialogContentProps, type Content } from '@radix-ui/react-dialog';
import './Modal.scss';

export interface BaseModalProps {
	children: React.ReactNode;
	className?: string;
	modalBodyClassname?: string;
	noPadding?: boolean;
	contentClassname?: string;
	description?: string | React.ReactNode;
	footerDescription?: string | React.ReactNode;
	footerActions?: React.ReactNode;
	footerActionPosition?: 'left' | 'center' | 'right';
	footerVariant?: 'default' | 'fixed' | 'calculator';
	hideModalTrigger?: boolean;
	hideModalClose?: boolean;
	modalCloseAsChild?: boolean;
	modalCloseChild?: React.ReactNode;
	modalCloseText?: string;
	modalTriggerAsChild?: boolean;
	modalTriggerChild?: React.ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	onInteractOutside?: React.ComponentPropsWithoutRef<typeof Content>['onInteractOutside'];
	onEscapeKeyDown?: React.ComponentPropsWithoutRef<typeof Content>['onEscapeKeyDown'];
	size?: 'xs' | 'sm' | 'md' | 'lg';
	subtitle?: string | React.ReactNode;
	title?: string; // TODO: This should be required as its an accessibility requirement, you can use the hideTitle prop to hide it visually
	hideTitle?: boolean;
	testid?: string;
	onCloseAutoFocus?: (event: Event) => void;
	preserveScrollPosition?: boolean;
	canBeClosed?: boolean;
	onOpenAutoFocus?: DialogContentProps['onOpenAutoFocus'];
}

interface DefaultModalProps extends BaseModalProps {
	variant?: 'default' | 'compact' | 'flyout' | 'navbar' | 'overlay';
}

interface SplitModalProps extends BaseModalProps {
	variant: 'split';
	splitChildren: React.ReactNode;
}

export type ModalProps = DefaultModalProps | SplitModalProps;

const isSplitModal = (props: ModalProps): props is SplitModalProps => props.variant === 'split' && 'splitChildren' in props;

export const Modal = (props: ModalProps) => {
	const {
		children,
		className,
		contentClassname,
		description,
		footerDescription,
		footerActions,
		footerActionPosition = 'left',
		footerVariant = 'default',
		hideModalTrigger = false,
		modalCloseAsChild,
		modalCloseChild,
		modalCloseText,
		modalTriggerAsChild,
		modalTriggerChild,
		open,
		onOpenChange,
		onOpenAutoFocus,
		size = 'md',
		subtitle,
		title,
		hideTitle,
		variant = 'default',
		testid,
		onCloseAutoFocus,
		preserveScrollPosition = false,
		canBeClosed = true
	} = props;

	let { hideModalClose = false, onInteractOutside, onEscapeKeyDown } = props;

	const modalBodyRef = useRef<HTMLDivElement>(null);
	const scrollPositionRef = useRef<number>(0);

	// Restore scroll position when content changes
	useLayoutEffect(() => {
		if (preserveScrollPosition && modalBodyRef.current && scrollPositionRef.current > 0) {
			modalBodyRef.current.scrollTop = scrollPositionRef.current;
		}
	}, [children, preserveScrollPosition]);

	// Save scroll position with debouncing
	useEffect(() => {
		if (!preserveScrollPosition || !open) return;

		// Wait for the modal to be rendered in the DOM
		let handleScroll: ReturnType<typeof debounce> | undefined = undefined;
		const rafId = requestAnimationFrame(() => {
			if (!modalBodyRef.current) return;

			handleScroll = debounce(() => {
				if (modalBodyRef.current) {
					scrollPositionRef.current = modalBodyRef.current.scrollTop;
				}
			}, 500);

			const modalBody = modalBodyRef.current;
			modalBody.addEventListener('scroll', handleScroll, { passive: true });
		});

		return () => {
			cancelAnimationFrame(rafId);

			if (handleScroll) {
				modalBodyRef.current?.removeEventListener('scroll', handleScroll);
				handleScroll.cancel();
			}
		};
	}, [preserveScrollPosition, open]);

	// Reset scroll position when modal closes
	useEffect(() => {
		if (!open) {
			scrollPositionRef.current = 0;
		}
	}, [open]);

	const contentStyles = {
		default: '[ mt-layout-2xl ]',
		flyout: '[ mt-layout-2xl ]',
		compact: '[ mt-layout-2xl p-0 ]',
		navbar: '[ pb-8 px-6 ]',
		split: '[ mt-layout-2xl flex-1 ]',
		overlay: '[ m-layout-md lg:m-layout-2xl ]'
	};

	const showHeader = (title || subtitle || description) && !hideTitle;
	const showFooter = footerActions || footerDescription;

	// If the modal can't be closed, we want to prevent closing it by clicking outside or pressing the escape key, and also hide the close button.
	if (!canBeClosed) {
		hideModalClose = true;
		onInteractOutside = (event) => event.preventDefault();
		onEscapeKeyDown = (event) => event.preventDefault();
	}

	// Radix treats anything outside the dialog as a dismiss, including third-party widgets (survey popups) that stack
	// above the modal. Only our own backdrop should close it, so interacting with such a widget leaves the modal open.
	const handleInteractOutside: BaseModalProps['onInteractOutside'] = (event) => {
		const { target } = event;

		if (!(target instanceof Element) || !target.closest('.modal__overlay')) {
			event.preventDefault();
		}

		onInteractOutside?.(event);
	};

	return (
		<ModalRoot modal={true} open={open} onOpenChange={onOpenChange}>
			{!hideModalTrigger && <ModalTrigger asChild={modalTriggerAsChild}>{modalTriggerChild}</ModalTrigger>}
			<ModalContent
				onOpenAutoFocus={onOpenAutoFocus}
				className={clsx('[ modal ] [ d-flex ]', className)}
				data-size={size}
				data-testid={testid}
				data-variant={variant}
				overlayClassname="[ modal__overlay ]"
				onInteractOutside={handleInteractOutside}
				onEscapeKeyDown={onEscapeKeyDown}
				onCloseAutoFocus={onCloseAutoFocus}
				{...(!description && { 'aria-describedby': undefined })}
			>
				{isSplitModal(props) && (
					<div className="[ modal__content-wrapper ] [ d-none md:d-block ]" data-side="left">
						{props.splitChildren}
					</div>
				)}
				<div
					className={clsx('[ modal__content-wrapper ] [ d-flex d-flex-column ]', contentStyles[variant], contentClassname)}
					{...(variant === 'split' && { 'data-side': 'right' })}
				>
					{hideTitle && <ModalTitle className="[ sr-only ]">{title}</ModalTitle>}
					{showHeader && (
						<div className="[ modal__header ] [ px-layout-sm md:px-layout-xl ]" data-testid="modal_header">
							{title && (
								<ModalTitle
									className={clsx('[ text-heading-4 color-text-primary text-wrap ]', {
										'[ modal__title--with-close ]': !hideModalClose
									})}
									data-testid="title"
								>
									{title}
								</ModalTitle>
							)}
							{subtitle && (
								<h3 className="[ modal__subtitle ][ mt-layout-md text-heading-5 color-text-primary ]" data-testid="subTitle">
									<span>{subtitle}</span>
								</h3>
							)}
							{description && (
								<ModalDescription
									className="[ modal__description ][ mt-layout-sm text-body-base color-text-secondary ]"
									data-testid="description"
								>
									{description}
								</ModalDescription>
							)}
						</div>
					)}
					<div className="[ modal__content ] [ d-flex d-flex-column ]" data-testid="modal_content">
						<div
							ref={modalBodyRef}
							className="[ modal__body ] [ pt-layout-lg px-layout-sm md:px-layout-xl pb-layout-md md:pb-layout-xl text-body-base color-text-secondary ]"
						>
							{children}
						</div>
						{showFooter && (
							<div className="[ modal__footer ] [ d-flex d-flex-column gap-md ]" data-variant={footerVariant}>
								{footerDescription && (
									<p className="[ modal__footer-description ] [ text-body-base color-text-secondary ]">{footerDescription}</p>
								)}
								{footerActions && (
									<div
										className="[ modal__footer-ctas ] [ d-flex d-flex-column md:d-flex-row gap-sm md:gap-md ]"
										data-action-position={footerActionPosition}
									>
										{footerActions}
									</div>
								)}
							</div>
						)}
					</div>
				</div>
				{!hideModalClose && (
					<ModalClose
						aria-label={modalCloseText || 'Close modal'}
						// Since the ModalClose renders a button and we're also using the ControlButton component, we're setting the asChild prop to true to avoid nesting buttons
						asChild={true}
						className={clsx(modalCloseAsChild && '[ modal__close-as-child ]', '[ color-text-brand ]')}
					>
						{modalCloseAsChild ? (
							modalCloseChild
						) : (
							<ControlButton className="modal__close" icon="close" iconPosition="left" rounded={true} variant="secondary" />
						)}
					</ModalClose>
				)}
			</ModalContent>
		</ModalRoot>
	);
};
